import { Injector } from "@angular/core";
import { ColumnValue } from "../../models/column-value";
import { CustomDataColumn } from "../../models/custom-viewer-config";
import { PipeService } from "./pipe-service";
import { BDOCS_INTEGRATION_SERVICE, ENI_INTEGRATION_SERVICE, RRHH_INTEGRATION_SERVICE, SIGN_INTEGRATION_SERVICE, SIGNAFORM_INTEGRATION_SERVICE } from "../../models/alden-constants";
import { KeyValue } from "@angular/common";
import { Node, NodePaging } from "@alfresco/js-api";
import { KeyValueExtended } from "../../models/key-value-extended";

export abstract class PipeServiceAbstract implements PipeService {

    constructor(protected injector?: Injector) { }

    selectCustomPipe(column: CustomDataColumn, value: any, elements?: string): Promise<ColumnValue> {
        // Plugins
        const signaformColumnValuePromise: Promise<ColumnValue> = this.pluginSelectCustomPipe(column, value, elements, SIGNAFORM_INTEGRATION_SERVICE);
        if (signaformColumnValuePromise != null) {
            return signaformColumnValuePromise;
        }
        const signColumnValuePromise: Promise<ColumnValue> = this.pluginSelectCustomPipe(column, value, elements, SIGN_INTEGRATION_SERVICE);
        if (signColumnValuePromise != null) {
            return signColumnValuePromise;
        }
        const rrhhColumnValuePromise: Promise<ColumnValue> = this.pluginSelectCustomPipe(column, value, elements, RRHH_INTEGRATION_SERVICE);
        if (rrhhColumnValuePromise != null) {
            return rrhhColumnValuePromise;
        }
        const eniColumnValuePromise: Promise<ColumnValue> = this.pluginSelectCustomPipe(column, value, elements, ENI_INTEGRATION_SERVICE);
        if (eniColumnValuePromise != null) {
            return eniColumnValuePromise;
        }
        const bdocsColumnValuePromise: Promise<ColumnValue> = this.pluginSelectCustomPipe(column, value, elements, BDOCS_INTEGRATION_SERVICE);
        if (bdocsColumnValuePromise != null) {
            return bdocsColumnValuePromise;
        }

        // Por defecto
        const columnValue: ColumnValue = { value, tooltip: value };
        return Promise.resolve(columnValue);
    }

    selectCustomImagePipe(column: CustomDataColumn, value: any, elements?: string): Promise<ColumnValue> {
        // Plugins
        /*const signColumnValuePromise: Promise<ColumnValue> = this.pluginSelectCustomImagePipe(column, value, elements, SIGN_INTEGRATION_SERVICE);
        if(signColumnValuePromise != null) {
            return signColumnValuePromise;
        }*/

        // Por defecto
        if (column.dataListName && elements) {
            const elementsObj: { [datalistName: string]: KeyValue<string, string>[] } = JSON.parse(elements);
            const elementsOfDataList = elementsObj[column.dataListName];
            const status = value.split('/').pop().replace('.svg', '');
            return Promise.resolve({ value, tooltip: elementsOfDataList?.find((s) => s.key === status)?.value });
        }
        return Promise.resolve({ value, tooltip: null });
    }

    refreshMaps() { }

    /**
     * Ejecuta un pipe personalizado definido externamente mediante inyección dinámica.
     *
     * Este método permite delegar la transformación del valor de una columna en un
     * servicio externo, identificado por `serviceToken`. Utiliza el injector para
     * obtener la instancia del servicio y ejecutar su método `selectCustomPipe`.
     *
     * Si el servicio no existe o no puede obtenerse, se captura la excepción y se
     * devuelve `null` para evitar que la tabla falle durante el renderizado.
     *
     * @param column Configuración de la columna que requiere la transformación.
     * @param value Valor original que debe transformarse.
     * @param elements (Opcional) Datos auxiliares necesarios para el pipe.
     * @param serviceToken Token del servicio que implementa el pipe personalizado.
     * @returns Promise con el resultado del pipe o `null` si no se pudo ejecutar.
     */
    protected pluginSelectCustomPipe(column: CustomDataColumn, value: any, elements: string, serviceToken: any): Promise<any> {
        try {
            const service = this.injector.get<any>(serviceToken);
            return service.selectCustomPipe(column, value, elements);
        } catch (e) {
            console.warn("Exception while doing 'selectCustomPipe'", e);
            return null;
        }
    }

    /**
     * Ejecuta un pipe personalizado de tipo imagen mediante inyección dinámica.
     *
     * Este método permite delegar la transformación visual de una columna en un
     * servicio externo identificado por `serviceToken`. Utiliza el injector para
     * obtener la instancia del servicio y ejecutar su método `selectCustomImagePipe`,
     * encargado de generar el valor final que se mostrará como imagen en la tabla.
     *
     * Si el servicio no puede obtenerse o se produce un error durante la ejecución,
     * se captura la excepción y se devuelve `null` para evitar que la tabla falle.
     *
     * @param column Configuración de la columna que requiere la transformación.
     * @param value Valor original que debe transformarse.
     * @param elements (Opcional) Datos auxiliares necesarios para el pipe.
     * @param serviceToken Token del servicio que implementa el pipe personalizado.
     * @returns Promise con el resultado del pipe o `null` si no se pudo ejecutar.
     */
    protected pluginSelectCustomImagePipe(column: CustomDataColumn, value: any, elements: string, serviceToken: any): Promise<any> {
        try {
            const service = this.injector.get<any>(serviceToken);
            return service.selectCustomImagePipe(column, value, elements);
        } catch (e) {
            console.warn("Exception while doing 'selectCustomImagePipe'", e);
            return null;
        }
    }

    /**
    * Transforma un valor (o lista de valores) en su representación textual
    * utilizando un mapa clave‑valor y, si es necesario, consultando una data list.
    *
    * Este método recibe uno o varios identificadores y los convierte en sus
    * valores legibles correspondientes. Si el valor no está en caché
    * (`keyValueMap`), se consulta la data list mediante `searchKey`.
    *
    * El resultado se devuelve como un `ColumnValue` con:
    *  - `value`: texto truncado para mostrar en la tabla
    *  - `tooltip`: texto completo para mostrar en hover
    *
    * @param value Valor o lista de valores que deben transformarse.
    * @param columnValue Objeto `ColumnValue` que será actualizado y devuelto.
    * @param keyValueMap Caché local de claves y valores ya resueltos.
    * @param dataListName Nombre de la data list donde buscar claves no resueltas.
    * @param searchKey Función que busca una clave en la data list.
    * @returns Promise<ColumnValue> Objeto con el valor formateado y su tooltip.
    */
    protected async selectKeyValuePipe(
        value: any,
        columnValue: ColumnValue,
        keyValueMap: Record<string, string | null>,
        dataListName: string,
        searchKey: (key: string, dataListName: string) => Promise<any>,
    ): Promise<ColumnValue> {
        const keys: string[] = value ? (Array.isArray(value) ? value : [value]) : [];
        let values: string[] = [];

        if (keys) {
            for (const key of keys) {
                const entity = keyValueMap[key];
                if (entity !== undefined) {
                    if (entity == null) {
                        await this.getKeyValue(key, keyValueMap).then((res) => {
                            values.push(res);
                        });
                    } else {
                        values.push(entity);
                    }
                } else {
                    keyValueMap[key] = null;
                    await searchKey(key, dataListName).then((res: KeyValue<string, string>[]) => {
                        const value = res[0]?.value;
                        keyValueMap[key] = value ?? null;
                        values.push(value);
                    });
                }
            }
        }

        values = values.filter((entity) => entity?.trim().length > 0);
        value = values.join(', ');
        const tooltip = values.join(',\n');

        const maxLength = 33;
        columnValue = { value: value ? value.substring(0, maxLength) + (value.length > maxLength ? '...' : '') : '', tooltip };
        return Promise.resolve(columnValue);
    }

    /**
     * Espera a que un valor pendiente en el mapa `keyValueMap` sea resuelto.
     *
     * Este método se utiliza cuando una clave ya está marcada como existente
     * pero su valor aún no ha sido recuperado (valor `null`). Se realiza un
     * pequeño polling hasta que el valor esté disponible.
     *
     * @param key Clave cuyo valor debe obtenerse.
     * @param keyValueMap Mapa de claves y valores ya resueltos o en proceso.
     * @returns Promise<string> Valor asociado a la clave una vez disponible.
     */
    private getKeyValue(
        key: string,
        keyValueMap: Record<string, string | null>,
    ): Promise<string> {
        return new Promise<string>(async (resolve) => {
            let value = keyValueMap[key];
            if (value == null) {
                do {
                    await this.wait(1);
                    value = keyValueMap[key];
                } while (value == null);
                resolve(value);
            } else {
                resolve(value);
            }
        });
    }

    /**
     * Transforma un valor (o lista de valores) en su representación textual
     * resolviendo entidades a partir de sus UUID y utilizando un mapa de caché.
     *
     * Este método recibe uno o varios identificadores de entidad y los convierte
     * en su representación legible. Si la entidad no está en caché (`entityMap`),
     * se consulta mediante `getEntityByUuid`. Una vez obtenida la entidad, se
     * transforma a texto utilizando `getEntityString`.
     *
     * El resultado se devuelve como un `ColumnValue` con:
     *  - `value`: texto truncado para mostrar en la tabla
     *  - `tooltip`: texto completo para mostrar en hover
     *
     * @param value UUID o lista de UUIDs de entidades que deben transformarse.
     * @param columnValue Objeto `ColumnValue` que será actualizado y devuelto.
     * @param entityMap Caché local de entidades ya resueltas indexadas por UUID.
     * @param getEntityByUuid Función que obtiene una entidad a partir de su UUID.
     * @param getEntityString Función que transforma una entidad (`Node`) en su representación textual.
     * @returns Promise<ColumnValue> Objeto con el valor formateado y su tooltip.
     */
    protected async selectEntitiesPipe(
        value: any,
        columnValue: ColumnValue,
        entityMap: Record<string, Node | KeyValueExtended<string, string> | any | null>,
        getEntityByUuid: (uuid: string) => Promise<NodePaging | any>,
        getEntityString: (node: Node |  any) => string
    ): Promise<ColumnValue> {
        const entityUUIDs: string[] = value ? (Array.isArray(value) ? value : [value]) : [];
        let entities: string[] = [];

        if (entityUUIDs) {
            for (const entityUUID of entityUUIDs) {
                const entity = entityMap[entityUUID];
                if (entity !== undefined) {
                    if (entity == null) {
                        await this.getEntity(entityUUID, entityMap, getEntityString).then((res) => {
                            entities.push(res);
                        });
                    } else {
                        entities.push(getEntityString(entity));
                    }
                } else {
                    entityMap[entityUUID] = null;
                    await getEntityByUuid(entityUUID).then((res) => {
                        const entry = res instanceof NodePaging ? res.list?.entries?.[0]?.entry : Array.isArray(res) ? res[0] : res;
                        entityMap[entityUUID] = entry ?? null;
                        entities.push(getEntityString(entry));
                    });
                }
            }
        }

        entities = entities.filter((entity) => entity?.trim().length > 0);
        value = entities.join(', ');
        const tooltip = entities.join(',\n');

        const maxLength = 33;
        columnValue = { value: value ? value.substring(0, maxLength) + (value.length > maxLength ? '...' : '') : '', tooltip };
        return Promise.resolve(columnValue);
    }

    /**
     * Espera a que una entidad pendiente en el mapa `entityMap` sea resuelta.
     *
     * Este método se utiliza cuando un UUID ya está marcado como existente
     * pero su entidad aún no ha sido recuperada (valor `null`). Se realiza
     * un pequeño polling hasta que la entidad esté disponible y entonces
     * se transforma a su representación textual mediante `getEntityString`.
     *
     * @param entityUUID UUID de la entidad cuyo valor debe obtenerse.
     * @param entityMap Mapa de entidades ya resueltas o en proceso indexadas por UUID.
     * @param getEntityString Función que transforma una entidad (`Node`) en su representación textual.
     * @returns Promise<string> Representación textual de la entidad una vez disponible.
     */
    protected getEntity(
        entityUUID: string,
        entityMap: Record<string, Node | any | null>,
        getEntityString: (entity: Node | any) => string
    ): Promise<string> {
        return new Promise<string>(async (resolve) => {
            let entity = entityMap[entityUUID];
            if (entity == null) {
                do {
                    await this.wait(1);
                    entity = entityMap[entityUUID];
                } while (entity == null);
                resolve(getEntityString(entity));
            } else {
                resolve(getEntityString(entity));
            }
        });
    }

    /**
   * Espera el número de segundos indicado.
   *
   * Se utiliza como mecanismo de espera en procesos donde se requiere
   * polling o sincronización simple.
   *
   * @param seconds Número de segundos a esperar.
   * @returns Promise<void> Promesa que se resuelve tras el tiempo indicado.
   */
    private wait(seconds: number) {
        return new Promise<void>((resolve) => {
            setTimeout(() => {
                resolve();
            }, (seconds * 1000));
        });
    }

}